import { motion, type HTMLMotionProps } from 'motion/react'
import { REVEAL_EASE } from './variants'
import { useCompactViewport } from './useMediaQuery'

export interface HeroRevealProps extends Omit<HTMLMotionProps<'div'>, 'initial' | 'animate' | 'transition'> {
  children: React.ReactNode
  /** Seconds before this block starts. Stagger hero lines with 0, 0.1, 0.2 ... */
  delay?: number
  duration?: number
  distance?: number
}

/**
 * Mount-time fade-up for above-the-fold hero content.
 * Unlike <ScrollReveal> it does not wait for the viewport, so the hero
 * plays immediately on page load and on route change.
 */
export function HeroReveal({ children, delay = 0, duration = 0.8, distance = 20, ...rest }: HeroRevealProps) {
  // Static on phones (see ScrollReveal)
  const compact = useCompactViewport()
  if (compact) {
    return <motion.div {...rest}>{children}</motion.div>
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: distance }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration, delay, ease: REVEAL_EASE }}
      {...rest}
    >
      {children}
    </motion.div>
  )
}
